import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MenuItemDetail from './MenuItemDetail';
import { useDebounce } from 'use-debounce';
import SEO from './SEO';
import { absoluteUrl, buildBreadcrumbs } from '../utils/seoConfig';
import './CreatorPage.css';

const STOCK_HERO_IMAGES = [
  'https://storage.googleapis.com/freshmenulab/stock_hero_images/cooking-kitchen-1.jpg',
  'https://storage.googleapis.com/freshmenulab/stock_hero_images/fresh-ingredients-2.jpg',
  'https://storage.googleapis.com/freshmenulab/stock_hero_images/colorful-dishes-3.jpg',
  'https://storage.googleapis.com/freshmenulab/stock_hero_images/rustic-table-4.jpg',
  'https://storage.googleapis.com/freshmenulab/stock_hero_images/modern-plating-5.jpg',
  'https://storage.googleapis.com/freshmenulab/stock_hero_images/herbs-spices-6.jpg',
  'https://storage.googleapis.com/freshmenulab/stock_hero_images/baking-bread-7.jpg',
  'https://storage.googleapis.com/freshmenulab/stock_hero_images/healthy-bowl-8.jpg',
];

const CreatorPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const itemsPerPage = 20;

  useEffect(() => {
    fetchCreatorPage();
  }, [slug, debouncedSearch, currentPage]);

  const fetchCreatorPage = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/affiliate/page/${slug}?${params}`
      );

      if (response.status === 404) {
        setError('not_found');
        setLoading(false);
        return;
      }

      if (!response.ok) throw new Error('Failed to load page');

      const data = await response.json();
      setCreator(data.creator);
      setMenuItems(data.menuItems);
      setPagination(data.pagination);
      setError(null);
    } catch (err) {
      console.error('Error loading creator page:', err);
      setError('load_error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMenuItem = async (item) => {
    window.history.pushState(null, '', `/menu/${item._id}?creator=${slug}`);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/menu/${item._id}`);
      if (!response.ok) throw new Error('Failed to load recipe');
      const fullItem = await response.json();
      setSelectedItem(fullItem);
    } catch {
      setSelectedItem(item);
    }
  };

  const handleCloseMenuItem = () => {
    setSelectedItem(null);
    window.history.pushState(null, '', `/creator/${slug}`);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  if (loading && !creator) {
    return (
      <div className="creator-page creator-page--classic">
        <div className="creator-loading">
          <div className="creator-loading-spinner" />
          <p>Loading creator page...</p>
        </div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="creator-page creator-page--classic">
        <div className="creator-not-found">
          <i className="fas fa-user-slash"></i>
          <h2>Creator Not Found</h2>
          <p>This creator page doesn't exist or is no longer available.</p>
          <button onClick={() => navigate('/menu')} className="creator-browse-btn">
            Browse All Recipes
          </button>
        </div>
      </div>
    );
  }

  if (error === 'load_error') {
    return (
      <div className="creator-page creator-page--classic">
        <div className="creator-not-found">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>Something went wrong</h2>
          <p>We couldn't load this page. Please try again later.</p>
          <button onClick={() => fetchCreatorPage()} className="creator-browse-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const themeClass = `creator-page--${creator?.theme || 'classic'}`;
  const heroImage = creator?.heroImage || STOCK_HERO_IMAGES[0];
  const socialLinks = creator?.socialMedia || {};
  const hasSocials = Object.values(socialLinks).some(v => v);

  const creatorJsonLd = creator
    ? {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: creator.name,
        description: creator.headline || creator.bio || `${creator.name} on FreshFitLab`,
        url: absoluteUrl(`/creator/${creator.slug || creator._id}`),
        image: creator.logoImage,
        knowsAbout: ['Recipes', 'Cooking', 'Meal planning'],
        sameAs: [
          socialLinks.instagram && `https://instagram.com/${socialLinks.instagram}`,
          socialLinks.facebook && `https://facebook.com/${socialLinks.facebook}`,
          socialLinks.twitter && `https://twitter.com/${socialLinks.twitter}`,
          socialLinks.youtube && `https://youtube.com/${socialLinks.youtube}`,
          socialLinks.tiktok && `https://tiktok.com/@${socialLinks.tiktok}`,
        ].filter(Boolean),
        worksFor: { '@type': 'Organization', name: 'FreshFitLab', url: 'https://freshfitlab.io' },
      }
    : null;

  return (
    <>
    {creator && creatorJsonLd && (
      <SEO
        title={`${creator.name} — Recipe Creator`}
        description={
          (creator.headline || creator.bio || '').slice(0, 200) ||
          `${creator.name} is a recipe creator on FreshFitLab. Browse their menus and shop their recipes.`
        }
        canonical={`/creator/${creator.slug || creator._id}`}
        image={creator.logoImage}
        type="profile"
        jsonLd={[
          creatorJsonLd,
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Creators', url: '/creators' },
            { name: creator.name, url: `/creator/${creator.slug || creator._id}` },
          ]),
        ]}
      />
    )}
    <div className={`creator-page ${themeClass}`}>
      {/* Hero Section */}
      <section className="creator-hero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="creator-hero-overlay" />
        <div className="creator-hero-content">
          {creator?.logoImage ? (
            <img src={creator.logoImage} alt={creator.name} className="creator-avatar" />
          ) : (
            <div className="creator-avatar creator-avatar--placeholder">
              <span>{(creator?.name || 'C').charAt(0).toUpperCase()}</span>
            </div>
          )}
          <h1 className="creator-name">{creator?.name || 'Creator'}</h1>
          {creator?.headline && (
            <p className="creator-headline">{creator.headline}</p>
          )}
          {hasSocials && (
            <div className="creator-socials">
              {socialLinks.instagram && (
                <a href={`https://instagram.com/${socialLinks.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <i className="fab fa-instagram"></i>
                </a>
              )}
              {socialLinks.facebook && (
                <a href={socialLinks.facebook.startsWith('http') ? socialLinks.facebook : `https://facebook.com/${socialLinks.facebook}`} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <i className="fab fa-facebook"></i>
                </a>
              )}
              {socialLinks.twitter && (
                <a href={`https://twitter.com/${socialLinks.twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer" aria-label="X / Twitter">
                  <i className="fab fa-twitter"></i>
                </a>
              )}
              {socialLinks.tiktok && (
                <a href={`https://tiktok.com/@${socialLinks.tiktok.replace('@', '')}`} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                  <i className="fab fa-tiktok"></i>
                </a>
              )}
              {creator?.website && (
                <a href={creator.website.startsWith('http') ? creator.website : `https://${creator.website}`} target="_blank" rel="noopener noreferrer" aria-label="Website">
                  <i className="fas fa-globe"></i>
                </a>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Bio Section */}
      {creator?.bio && (
        <section className="creator-bio-section">
          <p className="creator-bio">{creator.bio}</p>
        </section>
      )}

      {/* Menu Section */}
      <section className="creator-menu-section">
        <div className="creator-menu-header">
          <h2>Recipes</h2>
          <div className="creator-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search recipes..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button className="creator-search-clear" onClick={() => { setSearchTerm(''); setCurrentPage(1); }}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="creator-loading">
            <div className="creator-loading-spinner" />
          </div>
        ) : menuItems.length === 0 ? (
          <div className="creator-empty">
            <i className="fas fa-utensils"></i>
            <p>{searchTerm ? 'No recipes match your search.' : 'No recipes yet. Check back soon!'}</p>
          </div>
        ) : (
          <>
            {pagination.totalPages > 1 && (
              <div className="creator-pagination">
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  <i className="fas fa-chevron-left"></i> Prev
                </button>
                <span>Page {currentPage} of {pagination.totalPages}</span>
                <button
                  disabled={currentPage >= pagination.totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Next <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}

            <div className="creator-menu-grid">
              {menuItems.map(item => (
                <div
                  className="creator-menu-card"
                  key={item._id}
                  onClick={() => handleSelectMenuItem(item)}
                >
                  <div className="creator-card-image-wrap">
                    <img
                      src={item.image || '/placeholder-recipe.png'}
                      alt={item.title}
                      className="creator-card-image"
                      loading="lazy"
                    />
                    {item.cuisine && <span className="creator-card-cuisine">{item.cuisine}</span>}
                  </div>
                  <div className="creator-card-body">
                    <h3 className="creator-card-title">{item.title}</h3>
                    <p className="creator-card-desc">{item.description}</p>
                    <div className="creator-card-meta">
                      {item.totalTime && (
                        <span><i className="fas fa-clock"></i> {item.totalTime}</span>
                      )}
                      {item.servings && (
                        <span><i className="fas fa-users"></i> {item.servings}</span>
                      )}
                      {item.difficulty && (
                        <span className={`creator-card-difficulty creator-card-difficulty--${item.difficulty}`}>
                          {item.difficulty}
                        </span>
                      )}
                    </div>
                    {item.nutrition?.Calories && (
                      <div className="creator-card-macros">
                        <span>{item.nutrition.Calories} cal</span>
                        {item.nutrition.Protein && <span>{item.nutrition.Protein}g protein</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <div className="creator-pagination">
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  <i className="fas fa-chevron-left"></i> Prev
                </button>
                <span>Page {currentPage} of {pagination.totalPages}</span>
                <button
                  disabled={currentPage >= pagination.totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Next <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Footer attribution */}
      <div className="creator-powered-by">
        Powered by <a href="/">FreshFitLab</a>
      </div>
    </div>

    {selectedItem && (
      <MenuItemDetail
        item={selectedItem}
        onClose={handleCloseMenuItem}
      />
    )}
    </>
  );
};

export { STOCK_HERO_IMAGES };
export default CreatorPage;
